import axios from 'axios';
import { isBlockingRequest, startBusy, endBusy } from '../jsx/components/common/Loader/busy';

const normalizeBaseUrl = (u) => String(u || '').replace(/\/+$/, '');

const makeApiV1Base = (rootUrl, fallback) => {
  const root = normalizeBaseUrl(rootUrl);
  if (root) return `${root}/api/v1`;
  return fallback;
};

const LMS_API_BASE_URL = makeApiV1Base(process.env.REACT_APP_LMS_API_URL, '/lms-api/v1');
const AUTH_API_BASE_URL = makeApiV1Base(process.env.REACT_APP_AUTH_API_URL, '/auth-api/v1');
const PLACEMENT_API_BASE_URL = makeApiV1Base(
  process.env.REACT_APP_PLACEMENT_API_URL,
  '/placement-api/v1'
);
const CMS_API_BASE_URL = makeApiV1Base(process.env.REACT_APP_CMS_API_URL, '/cms-api/v1');
const CHAT_API_BASE_URL = makeApiV1Base(process.env.REACT_APP_CHAT_API_URL, '/chat-api/v1');

export const axiosInstance = axios.create({
  baseURL: `${LMS_API_BASE_URL}/`,
  timeout: 15000,
});

export const axiosInstanceV2 = axios.create({
  baseURL: AUTH_API_BASE_URL,
  timeout: 15000,
});

export const axiosInstancejob = axios.create({
  baseURL: PLACEMENT_API_BASE_URL,
  timeout: 15000,
});

export const axiosInstanceCMS = axios.create({
  baseURL: CMS_API_BASE_URL,
  timeout: 15000,
});

export const axiosInstanceChat = axios.create({
  baseURL: CHAT_API_BASE_URL,
  timeout: 15000,
});

// Raised once when the session goes, and listened for by
// components/common/SessionExpired.jsx. An event rather than a redirect
// so the app can say what happened before it moves anyone.
export const SESSION_EXPIRED_EVENT = 'lms:session-expired';

let sessionExpiredAnnounced = false;

const announceSessionExpired = () => {
  // A page mid-load can fire several requests at once; they would each
  // raise this. The reader only needs telling once.
  if (sessionExpiredAnnounced) return;
  sessionExpiredAnnounced = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

// Raised when lms-api refuses a student whose batches have all ended
// (middlewares/requireActiveStudent.js answers 403 with exactly this
// message). Listened for by Student/Dashboard/StudentAccessBanner.jsx.
// The flag is kept as well, because the dashboard's first requests can
// come back before the banner has mounted to hear the event.
export const STUDENT_ACCESS_DISABLED_EVENT = 'lms:student-access-disabled';
export const STUDENT_ACCESS_DISABLED_MESSAGE =
  'Your access has been disabled because your enrolled batches have ended.';

let studentAccessDisabled = false;
export const isStudentAccessDisabled = () => studentAccessDisabled;

const announceStudentAccessDisabled = () => {
  if (studentAccessDisabled) return;
  studentAccessDisabled = true;
  window.dispatchEvent(new CustomEvent(STUDENT_ACCESS_DISABLED_EVENT));
};

const setupInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    let token = '';
    let refreshToken = '';

    if (localStorage.getItem('accessToken')) {
      token = localStorage.getItem('accessToken') || '';
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        config.headers['Authorization'] = '';
        // window.location.href = '/login';
      }
    }

    if (localStorage.getItem('refreshToken')) {
      refreshToken = localStorage.getItem('refreshToken') || '';
      if (refreshToken) {
        config.headers['x-refresh-token'] = `${refreshToken}`;
      } else {
        config.headers['x-refresh-token'] = '';
        // window.location.href = '/login';
      }
    }

    // Add Google Access Token if available
    const googleToken = localStorage.getItem('googleAccessToken');
    if (googleToken) {
      config.headers['x-google-access-token'] = googleToken;
    }

    config.headers['x-app-name'] = 'lms';
    config.params = config.params || {};
    
    // Handle limit clamping in params
    if (typeof config.params.limit !== 'undefined') {
      const n = parseInt(config.params.limit, 10);
      const clamped = Math.min(1000, Math.max(1, isNaN(n) ? 10 : n));
      config.params.limit = clamped;
    }
    
    // Clean up undefined studentId
    if (config.params.studentId === 'undefined' || config.params.studentId === '') {
      delete config.params.studentId;
    }

    /* The global overlay is raised from here, not from the pages,
       because this is the only place that can see EVERY request and
       the one thing that decides whether it blocks: whether it carries
       pagination. A page cannot know that about a call it delegates to
       a service, and forty pages each deciding for themselves is forty
       chances to decide differently.

       The flag is written onto the config so the response handler can
       undo exactly what the request did -- reading isBlockingRequest
       twice would double-count if anything mutated the config between
       the two. */
    config.__blocking = isBlockingRequest(config);
    if (config.__blocking) startBusy();

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      if (response?.config?.__blocking) endBusy();
      return response;
    },
    (error) => {
      // Released on failure too, or one 500 leaves the app behind a veil
      // it can never lift.
      if (error?.config?.__blocking) endBusy();
      const status = error?.response?.status;
      // 401 is the token being gone or stale. 403 is a permission the
      // account genuinely lacks, which is not a session problem, so it
      // is left to the caller.
      if (status === 401) {
        // Not on the sign-in request itself: a wrong password there is
        // a form error, not an expired session.
        const url = String(error?.config?.url || '');
        const isAuthAttempt = /login|signin|refresh-token/i.test(url);
        if (!isAuthAttempt) announceSessionExpired();
      }
      if (
        status === 403 &&
        error?.response?.data?.message === STUDENT_ACCESS_DISABLED_MESSAGE
      ) {
        announceStudentAccessDisabled();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Apply interceptors
setupInterceptors(axiosInstance);
setupInterceptors(axiosInstanceV2);
setupInterceptors(axiosInstanceCMS);
setupInterceptors(axiosInstancejob);
setupInterceptors(axiosInstanceChat);

export default axiosInstance;
